<template>
  <div>
    <SideBar :sidebar-open="sidebarOpen" @update:sidebar-open="(value) => (sidebarOpen = value)" />

    <div class="xl:pl-72">
      <div
        class="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-6 border-b border-white/5 bg-background px-4 shadow-sm sm:px-6 lg:px-8"
      >
        <button type="button" id="sidebar" class="-m-2.5 p-2.5 text-white xl:hidden" @click="sidebarOpen = true">
          <span class="sr-only">{{ t("common.openSidebar") }}</span>
          <Bars3Icon class="h-5 w-5" aria-hidden="true" />
        </button>

        <SearchBar :model-value="search" @update:model-value="(text) => (search = text)" />
      </div>

      <AdminHeader />

      <main class="flex flex-col space-y-8 px-12 py-10">
        <div class="flex items-start justify-between">
          <div class="flex flex-col space-y-3">
            <div class="flex items-center gap-3">
              <h1
                tabindex="-1"
                class="no-focus-outline scroll-m-20 text-3xl font-bold tracking-tight"
                ref="initialFocus"
              >
                {{ t("pages.knocs.heading.title") }}
              </h1>
              <a
                class="ml-2 inline-flex items-center gap-1 rounded-md border border-transparent bg-secondary px-2.5 py-0.5 text-xs font-semibold text-secondary-foreground transition-colors hover:bg-secondary/80 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                href="https://docs.knocknoc.io/books/admin-guide/chapter/knocs-backends"
                target="_blank"
                :aria-label="`${t('pages.knocs.docs')} (${t('common.opensInNewTab')})`"
              >
                {{ t("common.docs") }}
                <ArrowTopRightOnSquareIcon class="h-3 w-3" />
                <span class="sr-only">{{ t("common.opensInNewTab") }}</span>
              </a>
            </div>

            <div class="max-w-2xl">
              <p class="text-base leading-relaxed text-muted-foreground">
                {{ t("pages.knocs.heading.description") }}
              </p>
            </div>
          </div>

          <div class="flex-shrink-0">
            <router-link
              to="/admin/knocs/new"
              type="button"
              data-testid="create-knoc-link"
              class="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-sm ring-offset-background transition-all duration-200 hover:bg-primary/90 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
            >
              {{ t("pages.knocs.heading.actions.create") }}
            </router-link>
          </div>
        </div>

        <template v-if="knocs.length > 0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead :aria-sort="sortBy === 'Name' ? (sortDesc ? 'descending' : 'ascending') : undefined">
                  <SortButton @click="handleSortChange('Name')">
                    {{ t("pages.knocs.table.columns.Name") }}
                  </SortButton>
                </TableHead>
                <TableHead>
                  {{ t("pages.knocs.table.columns.Details") }}
                </TableHead>
                <TableHead :aria-sort="sortBy === 'UserCount' ? (sortDesc ? 'descending' : 'ascending') : undefined">
                  <SortButton @click="handleSortChange('UserCount')">
                    {{ t("pages.knocs.table.columns.UserCount") }}
                  </SortButton>
                </TableHead>
                <TableHead :aria-sort="sortBy === 'AgentName' ? (sortDesc ? 'descending' : 'ascending') : undefined">
                  <SortButton @click="handleSortChange('AgentName')">
                    {{ t("pages.knocs.table.columns.AgentName") }}
                  </SortButton>
                </TableHead>
                <TableHead
                  :aria-sort="sortBy === 'LastModifiedAt' ? (sortDesc ? 'descending' : 'ascending') : undefined"
                >
                  <SortButton @click="handleSortChange('LastModifiedAt')">
                    {{ t("pages.knocs.table.columns.LastModifiedAt") }}
                  </SortButton>
                </TableHead>
                <TableHead>
                  <span class="sr-only">{{ t("common.actions") }}</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow v-if="filteredData.length === 0">
                <TableCell class="text-center text-muted-foreground" colspan="3">
                  {{ t("pages.knocs.table.empty") }}
                </TableCell>
              </TableRow>
              <TableRow
                v-for="(knoc, index) in filteredData"
                :key="knoc.ID"
                class="cursor-pointer"
                @click="viewKnocId = knoc.ID"
              >
                <TableCell>
                  <span class="block">{{ knoc.Name }}</span>
                  <span
                    v-if="knoc.Children.some((child) => Object.keys(child.BackendConfig)[0] === 'panos')"
                    class="block text-sm text-muted-foreground"
                  >
                    {{ t("pages.knocs.table.rows.paloAlto") }}
                  </span>
                  <span
                    v-else-if="knoc.Children.some((child) => Object.keys(child.BackendConfig)[0] === 'sonicwall')"
                    class="block text-sm text-muted-foreground"
                  >
                    {{ t("pages.knocs.table.rows.sonicPassive") }}
                  </span>
                  <span
                    v-else-if="Object.keys(knoc.BackendConfig)[0] === 'allowlist'"
                    class="block text-sm text-muted-foreground"
                  >
                    {{ t("pages.knocs.table.rows.allowListPassive") }}
                  </span>
                  <span
                    v-else-if="Object.keys(knoc.BackendConfig)[0] === 'haproxy'"
                    class="block text-sm text-muted-foreground"
                  >
                    {{ t("pages.knocs.table.rows.haproxy") }}
                  </span>
                  <span
                    v-else-if="Object.keys(knoc.BackendConfig)[0] === 'knocproxy'"
                    class="block text-sm text-muted-foreground"
                  >
                    {{ t("pages.knocs.table.rows.haproxykat") }}
                  </span>
                  <span
                    v-else-if="Object.keys(knoc.BackendConfig)[0] === 'fortios'"
                    class="block text-sm text-muted-foreground"
                  >
                    {{ t("pages.knocs.table.rows.fortinetActive") }}
                  </span>
                  <span
                    v-else-if="Object.keys(knoc.BackendConfig)[0] === 'fortimanager'"
                    class="block text-sm text-muted-foreground"
                  >
                    {{ t("pages.knocs.table.rows.fortinetManager") }}
                  </span>
                  <span
                    v-else-if="Object.keys(knoc.BackendConfig)[0] === 'azure'"
                    class="block text-sm text-muted-foreground"
                  >
                    {{ t("pages.knocs.table.rows.azure") }}
                  </span>
                  <span
                    v-else-if="Object.keys(knoc.BackendConfig)[0] === 'panos'"
                    class="block text-sm text-muted-foreground"
                  >
                    {{
                      knoc?.AclConfig?.panos?.EDLName
                        ? t("pages.knocs.table.rows.paloAlto")
                        : t("pages.knocs.table.rows.paloAltoActive")
                    }}
                  </span>
                  <span
                    v-else-if="Object.keys(knoc.BackendConfig)[0] === 'firepower'"
                    class="block text-sm text-muted-foreground"
                  >
                    {{ t("pages.knocs.table.rows.firepower") }}
                  </span>
                  <span
                    v-else-if="Object.keys(knoc.BackendConfig)[0] === 'sonicwall'"
                    class="block text-sm text-muted-foreground"
                  >
                    {{
                      knoc?.AclConfig?.sonicwall?.DynamicGroup
                        ? t("pages.knocs.table.rows.sonicPassive")
                        : t("pages.knocs.table.rows.sonicwall")
                    }}
                  </span>
                  <span
                    v-else-if="Object.keys(knoc.BackendConfig)[0] === 'sophosutm'"
                    class="block text-sm text-muted-foreground"
                  >
                    {{ t("pages.knocs.table.rows.sophosutm") }}
                  </span>
                  <span
                    v-else-if="Object.keys(knoc.BackendConfig)[0] === 'sophossfos'"
                    class="block text-sm text-muted-foreground"
                  >
                    {{ t("pages.knocs.table.rows.sophossfos") }}
                  </span>
                  <span
                    v-else-if="Object.keys(knoc.BackendConfig)[0] === 'aws'"
                    class="block text-sm text-muted-foreground"
                  >
                    {{ t("pages.knocs.table.rows.aws") }}
                  </span>
                  <span
                    v-else-if="Object.keys(knoc.BackendConfig)[0] === 'script'"
                    class="block text-sm text-muted-foreground"
                  >
                    {{ t("pages.knocs.table.rows.script") }}
                  </span>
                  <span
                    v-else-if="Object.keys(knoc.BackendConfig)[0] === 'ipset'"
                    class="block text-sm text-muted-foreground"
                  >
                    {{ t("pages.knocs.table.rows.ipset") }}
                  </span>
                  <span
                    v-else-if="Object.keys(knoc.BackendConfig)[0] === 'pf'"
                    class="block text-sm text-muted-foreground"
                  >
                    {{ t("pages.knocs.table.rows.pf") }}
                  </span>
                  <span
                    v-else-if="Object.keys(knoc.BackendConfig)[0] === 'windows_firewall'"
                    class="block text-sm text-muted-foreground"
                  >
                    {{ t("pages.knocs.table.rows.windows_firewall") }}
                  </span>
                  <span
                    v-else-if="Object.keys(knoc.BackendConfig)[0] === 'digitalocean'"
                    class="block text-sm text-muted-foreground"
                  >
                    {{ t("pages.knocs.table.rows.digitalocean") }}
                  </span>
                </TableCell>
                <TableCell>
                  <span
                    v-if="knoc.Children.some((child) => Object.keys(child.BackendConfig)[0] === 'panos')"
                    class="inline-flex flex-col"
                  >
                    <span v-if="Boolean(knoc.URL)"> User URL: {{ knoc.URL }} </span>
                    <span>
                      EDL:
                      {{
                        knoc.Children.find((child) => Object.keys(child.BackendConfig)[0] === "panos").AclConfig.panos
                          .EDLName
                      }}
                      (on fw
                      {{
                        knoc.Children.find((child) => Object.keys(child.BackendConfig)[0] === "panos").BackendConfig
                          .panos.Hostname
                      }})
                    </span>
                  </span>
                  <span
                    v-if="knoc.Children.some((child) => Object.keys(child.BackendConfig)[0] === 'sonicwall')"
                    class="inline-flex flex-col"
                  >
                    <span v-if="Boolean(knoc.URL)"> User URL: {{ knoc.URL }} </span>
                    <span>
                      Dynamic Group:
                      {{
                        knoc.Children.find((child) => Object.keys(child.BackendConfig)[0] === "sonicwall").AclConfig
                          .sonicwall.DynamicGroup
                      }}
                      (on fw
                      {{
                        knoc.Children.find((child) => Object.keys(child.BackendConfig)[0] === "sonicwall").BackendConfig
                          .sonicwall.Hostname
                      }})
                    </span>
                  </span>
                  <span v-else-if="Object.keys(knoc.BackendConfig)[0] === 'allowlist'">
                    <CopySpan display="Allowlist (copy)" :value="aclFullUrl(knoc)" />
                  </span>
                  <span v-else-if="Object.keys(knoc.BackendConfig)[0] === 'haproxy'">
                    HAProxy: ACL ID {{ knoc.AclConfig.haproxy.ID }}
                  </span>
                  <span v-else-if="Object.keys(knoc.BackendConfig)[0] === 'knocproxy'">
                    HAProxy + KAT: ACL {{ knoc.AclConfig.knocproxy.Name }}
                  </span>
                  <span v-else-if="Object.keys(knoc.BackendConfig)[0] === 'fortios'">
                    Fortinet: Address group {{ knoc.AclConfig.fortios.Name }}
                  </span>
                  <span v-else-if="Object.keys(knoc.BackendConfig)[0] === 'fortimanager'">
                    FortiManager: Hostname {{ knoc.BackendConfig.fortimanager.Hostname }}
                  </span>
                  <span v-else-if="Object.keys(knoc.BackendConfig)[0] === 'azure'">
                    Azure: Resource group {{ knoc.AclConfig.azure.ResourceGroup }}
                  </span>
                  <span v-else-if="Object.keys(knoc.BackendConfig)[0] === 'panos'">
                    {{
                      knoc.AclConfig.panos.EDLName
                        ? `EDL: ${knoc.AclConfig.panos.EDLName} (on fw ${knoc.AclConfig.panos.URL})`
                        : `Group name ${knoc.AclConfig.panos.GroupName}`
                    }}
                  </span>
                  <span v-else-if="Object.keys(knoc.BackendConfig)[0] === 'firepower'">
                    Group UUID: {{ knoc.AclConfig.firepower.GroupUUID }}
                  </span>
                  <span v-else-if="Object.keys(knoc.BackendConfig)[0] === 'sonicwall'">
                    {{
                      knoc.AclConfig.sonicwall.DynamicGroup
                        ? `Dynamic Group: ${knoc.AclConfig.sonicwall.DynamicGroup}`
                        : `Group Names: ${knoc.AclConfig.sonicwall.IPv4Group}, ${knoc.AclConfig.sonicwall.IPv6Group}`
                    }}
                  </span>
                  <span v-else-if="Object.keys(knoc.BackendConfig)[0] === 'sophosutm'">
                    Group Ref: {{ knoc.AclConfig.sophosutm.GroupReference }}
                  </span>
                  <span v-else-if="Object.keys(knoc.BackendConfig)[0] === 'sophossfos'">
                    Group Name: {{ knoc.AclConfig.sophossfos.GroupName }}
                  </span>
                  <span v-else-if="Object.keys(knoc.BackendConfig)[0] === 'aws'">
                    Group Name: {{ knoc.AclConfig.aws.SecurityGroupId }}
                  </span>
                  <span v-else-if="Object.keys(knoc.BackendConfig)[0] === 'script'">
                    Run {{ knoc.BackendConfig.script.Command }}, passing
                    {{ knoc.BackendConfig.script.EnvVars?.length || 0 }} parameters
                  </span>
                  <div
                    v-else-if="Object.keys(knoc.BackendConfig)[0] === 'ipset'"
                    class="inline-flex flex-col space-y-1"
                  >
                    <span v-if="knoc.AclConfig.ipset.V4SetName">
                      Add IPv4 addresses to set '{{ knoc.AclConfig.ipset.V4SetName }}'
                    </span>
                    <span v-if="knoc.AclConfig.ipset.V6SetName">
                      Add IPv6 addresses to set '{{ knoc.AclConfig.ipset.V6SetName }}'
                    </span>
                  </div>
                  <div v-else-if="Object.keys(knoc.BackendConfig)[0] === 'pf'" class="inline-flex flex-col space-y-1">
                    <span> Add addresses to PF table '{{ knoc.AclConfig.pf.TableName }}' </span>
                  </div>
                  <div
                    v-else-if="Object.keys(knoc.BackendConfig)[0] === 'windows_firewall'"
                    class="inline-flex flex-col space-y-1"
                  >
                    <span> Add addresses to rule '{{ knoc.AclConfig.windows_firewall.RuleName }}' </span>
                  </div>
                  <div
                    v-else-if="Object.keys(knoc.BackendConfig)[0] === 'digitalocean'"
                    class="inline-flex flex-col space-y-1"
                  >
                    <span>FirewallID: {{ knoc.AclConfig.digitalocean.FirewallID }}</span>
                  </div>
                </TableCell>
                <TableCell>
                  <span class="block">{{ knoc.UserCount }} {{ t("common.users").toLowerCase() }}</span>
                  <span class="block">{{ knoc.GroupCount }} {{ t("common.groups").toLowerCase() }}</span>
                </TableCell>
                <TableCell>
                  {{ knoc.AgentName || "-" }}
                </TableCell>
                <TableCell>
                  <span class="block">
                    {{ knoc.LastModifiedAt ? DateTime.fromISO(knoc.LastModifiedAt).toRelative() : "-" }}
                  </span>
                  <span
                    v-if="Boolean(knoc.LastModifiedAt) && Boolean(knoc.LastModifiedBy)"
                    class="block text-xs text-muted-foreground"
                  >
                    by {{ knoc.LastModifiedBy }}
                  </span>
                </TableCell>
                <TableCell>
                  <Menu as="div" class="relative inline-block text-left">
                    <MenuButton
                      ref="menuButtonsRefs"
                      :aria-label="t('common.actions')"
                      class="inline-flex h-10 w-10 items-center justify-center gap-2 whitespace-nowrap rounded-full text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
                      @click="(e) => e.stopPropagation()"
                    >
                      <EllipsisVerticalIcon class="h-4 w-4" />
                    </MenuButton>

                    <Teleport to="body">
                      <MenuItems
                        as="div"
                        class="fixed z-[100] w-56 origin-top-right divide-y divide-gray-100 rounded-md bg-background shadow-lg ring-1 ring-black/5 focus:outline-none"
                        :style="{
                          top: (menuItemPositions[index]?.top || 0) + 40 + 'px',
                          left: (menuItemPositions[index]?.left || 0) - 120 + 'px',
                        }"
                      >
                        <div class="px-1 py-1">
                          <MenuItem>
                            <button
                              class="group flex w-full items-center rounded-md px-2 py-2 text-sm hover:bg-primary/20"
                              @click="
                                router.push(
                                  `/admin/knocs/${knoc.Children.some((child) => Object.keys(child.BackendConfig)[0] === 'panos' || Object.keys(child.BackendConfig)[0] === 'sonicwall') ? knoc.Children.find((child) => Object.keys(child.BackendConfig)[0] === 'panos' || Object.keys(child.BackendConfig)[0] === 'sonicwall').ID : knoc.ID}/update`,
                                )
                              "
                            >
                              <PencilSquareIcon class="mr-2 h-5 w-5" />
                              {{ t("common.edit") }}
                            </button>
                          </MenuItem>
                          <MenuItem>
                            <button
                              class="group flex w-full items-center rounded-md px-2 py-2 text-sm hover:bg-primary/20"
                              @click="openTestGrantDialog(knoc.ID)"
                            >
                              <ShieldCheckIcon class="mr-2 h-5 w-5" />
                              {{ t("common.addTestGrant") }}
                            </button>
                          </MenuItem>

                          <MenuItem>
                            <button
                              class="group flex w-full items-center rounded-md px-2 py-2 text-sm hover:bg-primary/20"
                              @click="deleteKnocId = knoc.ID"
                              aria-label="Delete knoc"
                            >
                              <TrashIcon class="mr-2 h-5 w-5" />
                              {{ t("common.delete") }}
                            </button>
                          </MenuItem>
                        </div>
                      </MenuItems>
                    </Teleport>
                  </Menu>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </template>

        <div v-if="knocs.length === 0" class="flex flex-col space-y-4">
          <h3 class="scroll-m-20 text-2xl font-semibold tracking-tight">
            {{ t("pages.knocs.wizard.title") }}
          </h3>

          <div class="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            <router-link
              @click="wizard.$reset()"
              to="/admin/knocs/new/linux"
              class="group flex h-[206px] cursor-pointer select-none flex-col space-y-6 rounded-lg border bg-background p-6 shadow-sm transition-colors hover:border-white"
            >
              <div class="space-y-2">
                <ServerIcon class="h-8 w-8 text-zinc-500 transition-colors group-hover:text-white" />

                <h4 class="text-2xl font-semibold leading-none tracking-tight text-white">
                  {{ t("pages.knocs.wizard.options.linux.title") }}
                </h4>

                <p class="text-sm text-muted-foreground">
                  {{ t("pages.knocs.wizard.options.linux.description.0") }}
                  <br />
                  {{ t("pages.knocs.wizard.options.linux.description.1") }}
                </p>
              </div>

              <p class="!mt-auto text-sm text-muted-foreground">
                {{ t("pages.knocs.wizard.options.linux.other") }}
              </p>
            </router-link>

            <router-link
              @click="wizard.$reset()"
              to="/admin/knocs/new/application"
              class="group flex h-[206px] cursor-pointer select-none flex-col space-y-6 rounded-lg border bg-background p-6 shadow-sm transition-colors hover:border-white"
            >
              <div class="space-y-2">
                <ShieldCheckIcon class="h-8 w-8 text-zinc-500 transition-colors group-hover:text-white" />

                <h4 class="text-2xl font-semibold leading-none tracking-tight text-white">
                  {{ t("pages.knocs.wizard.options.application.title") }}
                </h4>

                <p class="text-sm text-muted-foreground">
                  {{ t("pages.knocs.wizard.options.application.description.0") }}
                  <br />
                  {{ t("pages.knocs.wizard.options.application.description.1") }}
                </p>
              </div>

              <p class="!mt-auto text-sm text-muted-foreground">
                {{ t("pages.knocs.wizard.options.application.other") }}
              </p>
            </router-link>

            <router-link
              @click="wizard.$reset()"
              to="/admin/knocs/new/proxy"
              class="group flex h-[206px] cursor-pointer select-none flex-col space-y-6 rounded-lg border bg-background p-6 shadow-sm transition-colors hover:border-white"
            >
              <div class="space-y-2">
                <GlobeAltIcon class="h-8 w-8 text-zinc-500 transition-colors group-hover:text-white" />

                <h4 class="text-2xl font-semibold leading-none tracking-tight text-white">
                  {{ t("pages.knocs.wizard.options.proxy.title") }}
                </h4>

                <p class="text-sm text-muted-foreground">
                  {{ t("pages.knocs.wizard.options.proxy.description.0") }}
                  <br />
                  {{ t("pages.knocs.wizard.options.proxy.description.1") }}
                </p>
              </div>

              <p class="!mt-auto text-sm text-muted-foreground">
                {{ t("pages.knocs.wizard.options.proxy.other") }}
              </p>
            </router-link>

            <router-link
              class="group flex h-[206px] cursor-pointer select-none flex-col space-y-6 rounded-lg border bg-background p-6 shadow-sm transition-colors hover:border-white"
              data-testid="create-windwos-link"
              to="/admin/knocs/new/windows"
              @click="wizard.$reset()"
            >
              <div class="space-y-2">
                <CubeIcon class="h-8 w-8 text-zinc-500 transition-colors group-hover:text-white" />

                <h4 class="text-2xl font-semibold leading-none tracking-tight text-white">
                  {{ t("pages.knocs.wizard.options.windows.title") }}
                </h4>

                <p class="text-sm text-muted-foreground">
                  {{ t("pages.knocs.wizard.options.windows.description.0") }}
                  <br />
                  {{ t("pages.knocs.wizard.options.windows.description.1") }}
                </p>
              </div>

              <p class="!mt-auto text-sm text-muted-foreground">
                {{ t("pages.knocs.wizard.options.windows.other") }}
              </p>
            </router-link>

            <router-link
              @click="wizard.$reset()"
              to="/admin/knocs/new/script"
              class="group flex h-[206px] cursor-pointer select-none flex-col space-y-6 rounded-lg border bg-background p-6 shadow-sm transition-colors hover:border-white"
            >
              <div class="space-y-2">
                <CodeBracketIcon class="h-8 w-8 text-zinc-500 transition-colors group-hover:text-white" />

                <h4 class="text-2xl font-semibold leading-none tracking-tight text-white">
                  {{ t("pages.knocs.wizard.options.script.title") }}
                </h4>
              </div>

              <p class="!mt-auto text-sm text-muted-foreground">
                {{ t("pages.knocs.wizard.options.script.other") }}
              </p>
            </router-link>

            <a
              href="mailto:support@knocknoc.io?subject=Knoc: I have a custom integration request"
              class="group flex h-[206px] cursor-pointer select-none flex-col space-y-6 rounded-lg border bg-background p-6 shadow-sm transition-colors hover:border-white"
            >
              <div class="space-y-2">
                <ChatBubbleLeftRightIcon class="h-8 w-8 text-zinc-500 transition-colors group-hover:text-white" />

                <h4 class="text-2xl font-semibold leading-none tracking-tight text-white">
                  {{ t("pages.knocs.wizard.options.contact.title") }}
                </h4>
              </div>

              <p class="!mt-auto text-sm text-muted-foreground">
                {{ t("pages.knocs.wizard.options.contact.other") }}
              </p>
            </a>
          </div>
        </div>
      </main>
    </div>
  </div>

  <Dialog :show="Boolean(viewKnocId)">
    <DialogOverlay v-if="Boolean(viewKnocId)" />

    <DialogPanel v-if="viewKnoc" class="max-w-[768px]">
      <DialogTitle>{{ viewKnoc.Name }}</DialogTitle>

      <template
        v-if="
          viewKnoc.Children.some(
            (child) =>
              Object.keys(child.BackendConfig)[0] === 'panos' || Object.keys(child.BackendConfig)[0] === 'sonicwall',
          )
        "
      >
        <DialogDescription>
          A Passive+ Knoc includes two elements:
          <ul class="ml-4 mt-1 list-decimal">
            <li>
              Publishing an Allowlist that your firewall consumes periodically. This can be verified using:

              <CodeBlock :code="`curl -u apiuser:<apikey> ${aclFullUrl(viewKnoc)}`" class="my-4" />

              Note: the API key must be replaced with the correct key you copied on Knoc creation.
            </li>
            <li>An Active call to the firewall API to refresh the EDL/Allowlist, occurs during user login.</li>
          </ul>
        </DialogDescription>

        <DialogDescription>
          See:
          <a
            class="underline hover:no-underline"
            href="https://docs.knocknoc.io/books/admin-guide/page/palo-alto"
            target="_blank"
            tabindex="-1"
            aria-label="Palo alto documentation (opens in new tab)"
            >PaloAlto</a
          >{{ " " }}
          <a
            class="underline hover:no-underline"
            href="https://docs.knocknoc.io/books/admin-guide/page/allowlist-edls"
            target="_blank"
            tabindex="-1"
            aria-label="Allow list documentation (opens in new tab)"
            >Allowlist</a
          >{{ " " }}
          <a
            class="underline hover:no-underline"
            href="https://docs.knocknoc.io/books/admin-guide/page/fortigate-address-groups-fortinet"
            target="_blank"
            tabindex="-1"
            aria-label="Fortinet documentation (opens in new tab)"
            >Fortinet</a
          >{{ " " }}
          <a
            class="underline hover:no-underline"
            href="https://docs.knocknoc.io/books/admin-guide/chapter/knocs-backends"
            target="_blank"
            tabindex="-1"
            aria-label="Backnds and knocs documentation (opens in new tab)"
            >Cisco/Other</a
          >
        </DialogDescription>
      </template>
      <template v-else-if="Object.keys(viewKnoc.AclConfig)[0] === 'allowlist'">
        <DialogDescription>
          Authorized IP addresses will be added to the 'Allowlist' URI which is accessible
          <a :href="aclFullUrl(viewKnoc)" tabindex="-1">here</a>.
        </DialogDescription>

        <DialogDescription v-if="viewKnoc.AclConfig.allowlist.AllowAccessByIp">
          This can be verified using 'curl {{ aclFullUrl(viewKnoc) }}' from an IP address permitted to access the
          allowlist.
        </DialogDescription>

        <DialogDescription v-else>
          This can be verified using 'curl -u apiuser:&lt;apikey&gt; {{ aclFullUrl(viewKnoc) }}' where the API key is
          replaced with the correct key.
        </DialogDescription>

        <DialogDescription>
          This allowlist is then consumed by your polling firewall with inclusion in the relevant firewall policy.
        </DialogDescription>

        <DialogDescription>
          See:
          <a
            class="underline hover:no-underline"
            href="https://docs.knocknoc.io/books/admin-guide/page/allowlistedl-access"
            target="_blank"
            tabindex="-1"
            aria-label="Allow list documentation (opens in new tab)"
            >Allowlist</a
          >{{ " " }}
          <a
            class="underline hover:no-underline"
            href="https://docs.knocknoc.io/books/admin-guide/page/fortigate-address-groups-fortinet"
            target="_blank"
            tabindex="-1"
            aria-label="Fortigate address groups documentation (opens in new tab)"
            >Fortinet</a
          >{{ " " }}
          <a
            class="underline hover:no-underline"
            href="https://docs.knocknoc.io/books/admin-guide/page/palo-alto"
            target="_blank"
            tabindex="-1"
            aria-label="Palo alto documentation (opens in new tab)"
            >PaloAlto</a
          >
        </DialogDescription>
      </template>
      <template v-else-if="Object.keys(viewKnoc.BackendConfig)[0] === 'haproxy'">
        <DialogDescription>
          HAProxy will receive authorized IP address information via the ACL '{{ viewKnoc.AclConfig.haproxy.ID }}'.
        </DialogDescription>

        <span v-if="viewKnoc.BackendConfig?.haproxy?.Protocol === 'unix'">
          <p class="my-2">This can be verified by reading the HAProxy socket using socat:</p>
          <CodeBlock class="mt-4" :code="haproxyAdminCmd(viewKnoc)" />
        </span>

        <span v-else-if="viewKnoc.BackendConfig?.haproxy?.Protocol === 'tcp'">
          <p class="my-2">This can be verified by reading the HAProxy socket using netcat:</p>
          <CodeBlock class="mt-4" :code="haproxyAdminCmd(viewKnoc)" />
        </span>

        <DialogDescription>
          Suitable HAProxy configuration is then required to make use of this ACL.
        </DialogDescription>

        <DialogDescription>
          See:
          <a
            class="underline hover:no-underline"
            href="https://docs.knocknoc.io/books/admin-guide/page/haproxy"
            target="_blank"
            tabindex="-1"
            aria-label="Haproxy documentation (opens in new tab)"
            >HAProxy</a
          >
        </DialogDescription>
      </template>
      <template v-else-if="Object.keys(viewKnoc.BackendConfig)[0] === 'knocproxy'">
        <DialogDescription>
          <p class="my-2">
            To make use of this Knoc, copy the HAProxy configuration and
            <a
              class="underline hover:no-underline"
              href="https://docs.knocknoc.io/books/admin-guide/page/haproxy"
              target="_blank"
              tabindex="-1"
              aria-label="Haproxy documentation (opens in new tab)"
              >read about configuration</a
            >:
          </p>

          <p class="my-6">
            <CopyButton :value="haproxyConfig(viewKnoc)"> Copy HAProxy backend configuration </CopyButton>
          </p>

          <p>
            HAProxy will receive authorized client information via the map '{{ viewKnoc.AclConfig.knocproxy.Name }}'.
          </p>

          <span v-if="viewKnoc.BackendConfig?.knocproxy?.Protocol === 'unix'">
            <p class="my-2">This can be verified by reading the HAProxy socket using socat:</p>
            <CodeBlock :code="haproxyAdminCmd(viewKnoc)" class="my-2" />
          </span>

          <span v-else-if="viewKnoc.BackendConfig?.knocproxy?.Protocol === 'tcp'">
            <p class="my-2">This can be verified by reading the HAProxy socket using netcat:</p>
            <CodeBlock :code="haproxyAdminCmd(viewKnoc)" class="my-2" />
          </span>
        </DialogDescription>
      </template>
      <template v-else-if="Object.keys(viewKnoc.BackendConfig)[0] === 'fortios'">
        <DialogDescription>
          Authorized IP addresses will be added to the 'Fortinet' {{ viewKnoc.BackendConfig.fortios.Url }}
        </DialogDescription>

        <DialogDescription>
          This can be verified via the Fortinet management console and visiting Network Objects.
        </DialogDescription>

        <DialogDescription>
          Configure the firewall to make use of these source addresses in your allowlisting firewall policy.
        </DialogDescription>

        <DialogDescription>
          See:
          <a
            class="underline hover:no-underline"
            href="https://docs.knocknoc.io/books/admin-guide/page/fortigate-address-groups-fortinet"
            target="_blank"
            tabindex="-1"
            aria-label="Fortinet documentation (opens in new tab)"
            >Fortinet</a
          >{{ " " }}
          <a
            class="underline hover:no-underline"
            href="https://docs.knocknoc.io/books/admin-guide/page/palo-alto"
            target="_blank"
            tabindex="-1"
            aria-label="Alto alto documentation (opens in new tab)"
            >PaloAlto</a
          >{{ " " }}
          <a
            class="underline hover:no-underline"
            href="https://docs.knocknoc.io/books/admin-guide/page/juniper-srx"
            target="_blank"
            tabindex="-1"
            aria-label="Juniper srx with allow list documentation (opens in new tab)"
            >Other</a
          >
        </DialogDescription>
      </template>
      <template v-else-if="Object.keys(viewKnoc.BackendConfig)[0] === 'fortimanager'">
        <DialogDescription>
          Authorized IP addresses will be added to the administrators 'Trusted Hosts' at
          {{ viewKnoc.BackendConfig.fortimanager.Hostname }}
        </DialogDescription>

        <DialogDescription>
          This can be verified via the FortiManager management console and visiting the administrators 'Trusted Hosts'.
        </DialogDescription>

        <DialogDescription>
          Configure the firewall to make use of these source addresses in your allowlisting firewall policy.
        </DialogDescription>

        <DialogDescription>
          See:
          <a
            class="underline hover:no-underline"
            href="https://docs.knocknoc.io/books/admin-guide/page/fortigate-address-groups-fortinet"
            target="_blank"
            tabindex="-1"
            aria-label="Fortigate address groups documentation (opens in new tab)"
            >Fortinet</a
          >{{ " " }}
          <a
            class="underline hover:no-underline"
            href="https://docs.knocknoc.io/books/admin-guide/page/palo-alto"
            target="_blank"
            tabindex="-1"
            aria-label="Palo alto documentation (opens in new tab)"
            >PaloAlto</a
          >{{ " " }}
          <a
            class="underline hover:no-underline"
            href="https://docs.knocknoc.io/books/admin-guide/page/juniper-srx"
            target="_blank"
            tabindex="-1"
            aria-label="Juniper srx with allow list documentation (opens in new tab)"
            >Other</a
          >
        </DialogDescription>
      </template>
      <template v-else-if="Object.keys(viewKnoc.BackendConfig)[0] === 'panos'">
        <DialogDescription>
          Authorized IP addresses will be added to the 'Palo Alto' address group
          {{ viewKnoc.BackendConfig.panos.Hostname }}
        </DialogDescription>

        <DialogDescription>
          This can be verified via the Palo Alto dashboard and visiting Address Groups.
        </DialogDescription>

        <DialogDescription>
          Configure the firewall to make use of these source addresses in your allowlisting firewall policy.
        </DialogDescription>

        <DialogDescription>
          See:
          <a
            class="underline hover:no-underline"
            href="https://docs.knocknoc.io/books/admin-guide/page/palo-alto"
            target="_blank"
            tabindex="-1"
            aria-label="Palo alto documentation (opens in new tab)"
            >PaloAlto</a
          >{{ " " }}
          <a
            class="underline hover:no-underline"
            href="https://docs.knocknoc.io/books/admin-guide/page/fortigate-address-groups-fortinet"
            target="_blank"
            tabindex="-1"
            aria-label="Fortinet documentation (opens in new tab)"
            >Fortinet</a
          >{{ " " }}
          <a
            class="underline hover:no-underline"
            href="https://docs.knocknoc.io/books/admin-guide/page/juniper-srx"
            target="_blank"
            tabindex="-1"
            aria-label="Juniper srx with allow list documentation (opens in new tab)"
            >Other</a
          >
        </DialogDescription>
      </template>
      <template v-else-if="Object.keys(viewKnoc.BackendConfig)[0] === 'firepower'">
        <DialogDescription>
          Authorized IP addresses will be added to the Firepower network group
          {{ viewKnoc.BackendConfig.firepower.Hostname }}
        </DialogDescription>

        <DialogDescription> This can be verified via the Firepower dashboard and visiting networks. </DialogDescription>

        <DialogDescription>
          Configure the firewall to make use of these source addresses in your allowlisting firewall policy.
        </DialogDescription>

        <DialogDescription>
          See:
          <a
            class="underline hover:no-underline"
            href="https://docs.knocknoc.io/books/admin-guide/page/palo-alto"
            target="_blank"
            tabindex="-1"
            aria-label="Palo alto documentation (opens in new tab)"
            >PaloAlto</a
          >{{ " " }}
          <a
            class="underline hover:no-underline"
            href="https://docs.knocknoc.io/books/admin-guide/page/fortigate-address-groups-fortinet"
            target="_blank"
            tabindex="-1"
            aria-label="Fortigate address group documentation (opens in new tab)"
            >Fortinet</a
          >{{ " " }}
          <a
            class="underline hover:no-underline"
            href="https://docs.knocknoc.io/books/admin-guide/page/juniper-srx"
            target="_blank"
            tabindex="-1"
            aria-label="Juniper srx with allow list documentation (opens in new tab)"
            >Other</a
          >
        </DialogDescription>
      </template>
      <template v-else-if="Object.keys(viewKnoc.BackendConfig)[0] === 'sonicwall'">
        <DialogDescription>
          Authorized IP addresses will be added to the Sonicwall network group
          {{ viewKnoc.BackendConfig.sonicwall.Hostname }}
        </DialogDescription>

        <DialogDescription>
          Configure the firewall to make use of these source addresses in your allowlisting firewall policy.
        </DialogDescription>

        <DialogDescription>
          See:
          <a
            class="underline hover:no-underline"
            href="https://docs.knocknoc.io/books/admin-guide/page/palo-alto"
            target="_blank"
            tabindex="-1"
            aria-label="Palo alto documentation (opens in new tab)"
            >PaloAlto</a
          >{{ " " }}
          <a
            class="underline hover:no-underline"
            href="https://docs.knocknoc.io/books/admin-guide/page/fortigate-address-groups-fortinet"
            target="_blank"
            tabindex="-1"
            aria-label="Fortigate address group documentation (opens in new tab)"
            >Fortinet</a
          >{{ " " }}
          <a
            class="underline hover:no-underline"
            href="https://docs.knocknoc.io/books/admin-guide/page/juniper-srx"
            target="_blank"
            tabindex="-1"
            aria-label="Juniper srx with allow list documentation (opens in new tab)"
            >Other</a
          >
        </DialogDescription>
      </template>
      <template v-else-if="Object.keys(viewKnoc.BackendConfig)[0] === 'sophosutm'">
        <DialogDescription>
          Authorized IP addresses will be added to the Sophos network group
          {{ viewKnoc.BackendConfig.sophosutm.Hostname }}
        </DialogDescription>

        <DialogDescription> This can be verified via the Sophos dashboard and visiting networks. </DialogDescription>

        <DialogDescription>
          Configure the firewall to make use of these source addresses in your allowlisting firewall policy.
        </DialogDescription>

        <DialogDescription>
          See:
          <a
            class="underline hover:no-underline"
            href="https://docs.knocknoc.io/books/admin-guide/page/palo-alto"
            target="_blank"
            tabindex="-1"
            aria-label="Palo alto documentation (opens in new tab)"
            >PaloAlto</a
          >{{ " " }}
          <a
            class="underline hover:no-underline"
            href="https://docs.knocknoc.io/books/admin-guide/page/fortigate-address-groups-fortinet"
            target="_blank"
            tabindex="-1"
            aria-label="Fortinet documentation (opens in new tab)"
            >Fortinet</a
          >{{ " " }}
          <a
            class="underline hover:no-underline"
            href="https://docs.knocknoc.io/books/admin-guide/page/juniper-srx"
            target="_blank"
            tabindex="-1"
            aria-label="Juniper srx with allow list documentation (opens in new tab)"
            >Other</a
          >
        </DialogDescription>
      </template>
      <template v-else-if="Object.keys(viewKnoc.BackendConfig)[0] === 'sophossfos'">
        <DialogDescription>
          Authorized IP addresses will be added to the Sophos IP host group
          {{ viewKnoc.BackendConfig.sophossfos.Hostname }}
        </DialogDescription>

        <DialogDescription>
          This can be verified via the Sophos dashboard and visiting Hosts and services.
        </DialogDescription>

        <DialogDescription>
          Configure the firewall to make use of these source addresses in your allowlisting firewall policy.
        </DialogDescription>

        <DialogDescription>
          See:
          <a
            class="underline hover:no-underline"
            href="https://docs.knocknoc.io/books/admin-guide/page/palo-alto"
            target="_blank"
            tabindex="-1"
            aria-label="Palo alto documentation (opens in new tab)"
            >PaloAlto</a
          >{{ " " }}
          <a
            class="underline hover:no-underline"
            href="https://docs.knocknoc.io/books/admin-guide/page/fortigate-address-groups-fortinet"
            target="_blank"
            tabindex="-1"
            aria-label="Fortigate address group documentation (opens in new tab)"
            >Fortinet</a
          >{{ " " }}
          <a
            class="underline hover:no-underline"
            href="https://docs.knocknoc.io/books/admin-guide/page/juniper-srx"
            target="_blank"
            tabindex="-1"
            aria-label="Juniper srx with allow list documentation (opens in new tab)"
            >Other</a
          >
        </DialogDescription>
      </template>
      <template v-else-if="Object.keys(viewKnoc.BackendConfig)[0] === 'azure'">
        <DialogDescription>
          Authorized IP addresses will be added to the Azure resource group
          {{ viewKnoc.BackendConfig.azure.ResourceGroup }}
        </DialogDescription>

        <DialogDescription>
          Configure the firewall to make use of these source addresses in your allowlisting firewall policy.
        </DialogDescription>

        <DialogDescription>
          See:
          <a
            class="underline hover:no-underline"
            href="https://docs.knocknoc.io/books/admin-guide/page/palo-alto"
            target="_blank"
            tabindex="-1"
            aria-label="Palo alto documentation (opens in new tab)"
            >PaloAlto</a
          >{{ " " }}
          <a
            class="underline hover:no-underline"
            href="https://docs.knocknoc.io/books/admin-guide/page/fortigate-address-groups-fortinet"
            target="_blank"
            tabindex="-1"
            aria-label="Fortigate address group documentation (opens in new tab)"
            >Fortinet</a
          >{{ " " }}
          <a
            class="underline hover:no-underline"
            href="https://docs.knocknoc.io/books/admin-guide/page/juniper-srx"
            target="_blank"
            tabindex="-1"
            aria-label="Juniper srx with allow list documentation (opens in new tab)"
            >Other</a
          >
        </DialogDescription>
      </template>
      <template v-else-if="Object.keys(viewKnoc.BackendConfig)[0] === 'aws'">
        <DialogDescription>
          Authorized IP addresses will be added to the AWS EC2 resource group
          {{ viewKnoc.AclConfig.aws.SecurityGroupId }} for {{ viewKnoc.AclConfig.aws.Direction }}
          {{ viewKnoc.AclConfig.aws.Protocol }} port '{{ viewKnoc.AclConfig.aws.Port }}'.
        </DialogDescription>

        <DialogDescription>
          Configure the firewall to make use of these source addresses in your allowlisting firewall policy.
        </DialogDescription>

        <DialogDescription>
          See:
          <a
            class="underline hover:no-underline"
            href="https://docs.knocknoc.io/books/admin-guide/page/palo-alto"
            target="_blank"
            tabindex="-1"
            aria-label="Palo alto documentation (opens in new tab)"
            >PaloAlto</a
          >{{ " " }}
          <a
            class="underline hover:no-underline"
            href="https://docs.knocknoc.io/books/admin-guide/page/fortigate-address-groups-fortinet"
            target="_blank"
            tabindex="-1"
            aria-label="Fortigate address group documentation (opens in new tab)"
            >Fortinet</a
          >{{ " " }}
          <a
            class="underline hover:no-underline"
            href="https://docs.knocknoc.io/books/admin-guide/page/juniper-srx"
            target="_blank"
            tabindex="-1"
            aria-label="Juniper srx with allow list documentation (opens in new tab)"
            >Other</a
          >
        </DialogDescription>
      </template>
      <template v-else-if="Object.keys(viewKnoc.BackendConfig)[0] === 'script'">
        <DialogDescription>
          The custom script '{{ viewKnoc.BackendConfig.script.Command }}' on Agent '{{ viewKnoc.AgentName }}' will
          receive the parameters 'add &lt;ipaddress&gt; {{ viewKnoc.Name }}' when an authorized user logs in.
        </DialogDescription>

        <DialogDescription>
          Note that environment variables are also available, along with sensitive values.
        </DialogDescription>

        <DialogDescription>
          See:
          <a
            class="underline hover:no-underline"
            href="https://docs.knocknoc.io/books/admin-guide/page/custom-script"
            target="_blank"
            tabindex="-1"
            aria-label="Script with arbitrary backend documentation (opens in new tab)"
            >Custom script</a
          >
        </DialogDescription>
      </template>
      <template v-else-if="Object.keys(viewKnoc.BackendConfig)[0] === 'ipset'">
        <DialogDescription>
          Authorized IP addresses will be added to the
          {{
            `${viewKnoc.AclConfig.ipset.V4SetName ? `'${viewKnoc.AclConfig.ipset.V4SetName}' IPSet (v4)` : ""}${
              viewKnoc.AclConfig.ipset.V4SetName && viewKnoc.AclConfig.ipset.V6SetName ? " and " : ""
            }${viewKnoc.AclConfig.ipset.V6SetName ? `'${viewKnoc.AclConfig.ipset.V6SetName}' IPSet (v6)` : ""},`
          }}
          which can be verified using '/usr/sbin/ipset -L'.
        </DialogDescription>

        <DialogDescription>
          The IPSet name then needs to be utilised in your Linux firewall or IPTables in the appropriate rule or policy.
        </DialogDescription>

        <DialogDescription>
          See:
          <a
            class="underline hover:no-underline"
            href="https://docs.knocknoc.io/books/admin-guide/page/ipset-linux-netfilteriptables"
            target="_blank"
            tabindex="-1"
            aria-label="Ipset documentation (opens in new tab)"
            >Linux</a
          >{{ " " }}
          <a
            class="underline hover:no-underline"
            href="https://docs.knocknoc.io/books/admin-guide/page/ipsets-with-ufw"
            target="_blank"
            tabindex="-1"
            >uFW</a
          >{{ " " }}
          <a
            class="underline hover:no-underline"
            href="https://docs.knocknoc.io/books/admin-guide/page/ipsets-with-shorewall"
            target="_blank"
            tabindex="-1"
            >Shorewall</a
          >
        </DialogDescription>
      </template>

      <template v-else-if="Object.keys(viewKnoc.BackendConfig)[0] === 'pf'">
        <DialogDescription>
          Authorized IP addresses will be added to the '{{ viewKnoc.AclConfig.pf.TableName }}' PF table, which can be
          verified using 'pfctl -t {{ viewKnoc.AclConfig.pf.TableName }} -T show'.
        </DialogDescription>

        <DialogDescription>
          The PF table then needs to be referenced in your pf.conf rules to allow or block traffic.
        </DialogDescription>

        <DialogDescription>
          <!-- TODO(shh) - Fix this -->
          See:
          <a
            class="underline hover:no-underline"
            href="https://docs.knocknoc.io/books/admin-guide/page/pf-packet-filter"
            target="_blank"
            tabindex="-1"
            aria-label="PF documentation (opens in new tab)"
            >PF documentation</a
          >
        </DialogDescription>
      </template>

      <template v-else-if="Object.keys(viewKnoc.BackendConfig)[0] === 'windows_firewall'">
        <DialogDescription>
          Authorized IP addresses will be added to the '{{ viewKnoc.AclConfig.windows_firewall.RuleName }}' rule which
          can be verified using the admin ui or via powershell

          <CodeBlock
            :code="`Get-NetFirewallRule -DisplayName '${viewKnoc.AclConfig.windows_firewall.RuleName}' | ForEach-Object { [PSCustomObject]@{ Rule = $_.DisplayName; RemoteIPs = ($_ | Get-NetFirewallAddressFilter).RemoteAddress } }`"
            class="my-4"
          />
        </DialogDescription>

        <DialogDescription> This rule will allow or deny the user access based on its configuration </DialogDescription>

        <DialogDescription>
          See:
          <a
            aria-label="Windows firewall documentation (opens in new tab)"
            class="underline hover:no-underline"
            href="https://docs.knocknoc.io/books/admin-guide/page/windows-firewall"
            tabindex="-1"
            target="_blank"
            >Windows firewall</a
          >
        </DialogDescription>
      </template>

      <template v-else-if="Object.keys(viewKnoc.BackendConfig)[0] === 'cloudflarelist'">
        <DialogDescription>
          <template v-if="viewKnoc.BackendConfig.cloudflarelist.ListName">
            Authorized IP addresses will be added to Cloudflare Rule List named
            {{ viewKnoc.BackendConfig.cloudflarelist.ListName }}.
          </template>
          <template v-else>
            Authorized IP addresses will be added to Cloudflare Rule List specified in the Knoc configuration.
          </template>
        </DialogDescription>

        <DialogDescription>
          This can be verified in the cloudflare dashboard at
          <a href="https://dash.cloudflare.com" target="_blank">https://dash.cloudflare.com/</a>. Check under Manage
          account > Configurations > Lists.
        </DialogDescription>

        <DialogDescription> No further configuration is required, just sit back and enjoy! </DialogDescription>
      </template>

      <template v-else-if="Object.keys(viewKnoc.BackendConfig)[0] === 'digitalocean'">
        <DialogDescription>
          Authorized IP addresses will be added to the DigitalOcean firewall with the id
          {{ viewKnoc.BackendConfig.digitalocean.FirewallID }}
        </DialogDescription>

        <DialogDescription>No further configuration is required, just sit back and enjoy!</DialogDescription>
      </template>

      <div class="flex justify-end space-x-2">
        <button
          data-testid="done-button"
          type="button"
          class="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-input bg-background px-4 py-2 text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 sm:mt-0 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
          @click="viewKnocId = null"
        >
          {{ t("common.done") }}
        </button>

        <Menu as="div" class="relative inline-block text-left">
          <MenuButton
            class="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground ring-offset-background transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
          >
            {{ t("common.actions") }}
            <ChevronDownIcon class="h-4 w-4" />
          </MenuButton>

          <MenuItems
            as="div"
            class="absolute right-0 mt-2 w-56 origin-top-right divide-y divide-gray-100 rounded-md bg-background shadow-lg ring-1 ring-black/5 focus:outline-none"
          >
            <div class="px-1 py-1">
              <MenuItem>
                <button
                  class="group flex w-full items-center rounded-md px-2 py-2 text-sm hover:bg-primary/20"
                  @click="
                    router.push(
                      `/admin/knocs/${viewKnoc.Children.some((child) => Object.keys(child.BackendConfig)[0] === 'panos') ? viewKnoc.Children.find((child) => Object.keys(child.BackendConfig)[0] === 'panos').ID : viewKnoc.ID}/update`,
                    )
                  "
                >
                  <PencilSquareIcon class="mr-2 h-5 w-5" />
                  {{ t("common.edit") }}
                </button>
              </MenuItem>
              <MenuItem>
                <button
                  class="group flex w-full items-center rounded-md px-2 py-2 text-sm hover:bg-primary/20"
                  @click="openTestGrantDialog(viewKnoc.ID)"
                >
                  <ShieldCheckIcon class="mr-2 h-5 w-5" />
                  {{ t("common.addTestGrant") }}
                </button>
              </MenuItem>
              <MenuItem>
                <button
                  class="group flex w-full items-center rounded-md px-2 py-2 text-sm hover:bg-primary/20"
                  @click="
                    deleteKnocId = viewKnocId;
                    viewKnocId = null;
                  "
                  aria-label="Delete knoc"
                >
                  <TrashIcon class="mr-2 h-5 w-5" />
                  {{ t("common.delete") }}
                </button>
              </MenuItem>
            </div>
          </MenuItems>
        </Menu>
      </div>
    </DialogPanel>
  </Dialog>

  <Dialog :show="Boolean(deleteKnocId)">
    <DialogOverlay v-if="Boolean(deleteKnocId)" />

    <DialogPanel>
      <DialogTitle>{{ t("pages.knocs.dialogs.delete.heading") }}</DialogTitle>

      <DialogDescription>
        {{ t("pages.knocs.dialogs.delete.description") }}
      </DialogDescription>

      <div class="flex justify-end space-x-2">
        <button
          class="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-input bg-background px-4 py-2 text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 sm:mt-0 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
          type="button"
          @click="deleteKnocId = null"
        >
          {{ t("common.cancel") }}
        </button>

        <button
          data-testid="delete-button"
          type="button"
          class="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground ring-offset-background transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
          @click="handleDeleteKnoc"
        >
          {{ t("common.delete") }}
        </button>
      </div>
    </DialogPanel>
  </Dialog>

  <Dialog :show="Boolean(apiKeyToDelete)">
    <DialogOverlay v-if="Boolean(apiKeyToDelete)" />

    <DialogPanel>
      <DialogTitle>
        {{ t("pages.knocs.dialogs.deleteApiKey.heading") }}
      </DialogTitle>

      <DialogDescription>
        {{ t("pages.knocs.dialogs.deleteApiKey.description") }}
      </DialogDescription>

      <div class="flex justify-end space-x-2">
        <button
          class="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-input bg-background px-4 py-2 text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 sm:mt-0 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
          type="button"
          @click="apiKeyToDelete = null"
        >
          {{ t("common.cancel") }}
        </button>

        <button
          data-testid="delete-button"
          type="button"
          class="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground ring-offset-background transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
          @click="handleDeleteApiKey"
        >
          {{ t("common.delete") }}
        </button>
      </div>
    </DialogPanel>
  </Dialog>

  <TestGrantDialog
    :knoc-id="testGrantKnocId"
    :show="showTestGrantDialog"
    @error="handleTestGrantError"
    @success="handleTestGrantSuccess"
    @update:show="(value) => (showTestGrantDialog = value)"
  />

  <ErrorNotification
    :error="errorMessage"
    :title="t('common.error')"
    @update:error="(input) => (errorMessage = input)"
  />

  <InfoNotification :text="infoMessage" @update:info="(input) => (infoMessage = input)" />
</template>

<script lang="ts" setup>
import { DialogDescription, Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/vue";
import { ArrowTopRightOnSquareIcon, Bars3Icon, EllipsisVerticalIcon } from "@heroicons/vue/24/solid";
import {
  ChatBubbleLeftRightIcon,
  ChevronDownIcon,
  CodeBracketIcon,
  CubeIcon,
  GlobeAltIcon,
  PencilSquareIcon,
  ServerIcon,
  ShieldCheckIcon,
  TrashIcon,
} from "@heroicons/vue/24/outline";
import { DateTime } from "luxon";
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";

import globals from "../../globals";
import userService from "../../services/user.service.ts";
import { useWizardStore } from "../../pinia/wizard";

import SearchBar from "../../components/SearchBar.vue";
import SideBar from "../../components/SideBar.vue";
import ErrorNotification from "../../components/ErrorNotification.vue";
import InfoNotification from "../../components/InfoNotification.vue";
import CopySpan from "../../components/CopySpan.vue";

import { Dialog, DialogOverlay, DialogPanel, DialogTitle } from "../../components/ui/dialog";
import { SortButton, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import CopyButton from "../../components/CopyButton.vue";
import AdminHeader from "../../components/AdminHeader.vue";
import CodeBlock from "../../components/CodeBlock.vue";
import { useUserStore } from "../../pinia/user.ts";
import TestGrantDialog from "./Components/TestGrantDialog.vue";

const router = useRouter();
const route = useRoute();
const { t } = useI18n();

const userStore = useUserStore();

const initialFocus = ref(null);

const sidebarOpen = ref(false);
const search = ref("");
const errorMessage = ref("");
const infoMessage = ref("");

const wizard = useWizardStore();

const knocs = ref([]);
const backends = ref([]);
const groups = ref([]);
const apiKeys = ref([]);

const menuButtonsRefs = ref([]);
const menuItemPositions = ref([]);
function updateMenuItemPositions() {
  menuItemPositions.value = menuButtonsRefs.value.map((ref) => ref.el.getBoundingClientRect());
}

const sortBy = ref("Name");
const sortDesc = ref(false);

function handleSortChange(column) {
  if (sortBy.value === column) {
    sortDesc.value = !sortDesc.value;
    return;
  }

  sortBy.value = column;
  sortDesc.value = false;
}

const filteredData = computed(() => {
  let children = knocs.value.filter((k) => k.ParentID !== null);
  const gravidKnocs = knocs.value.map((k) => {
    let kChildren = [];
    for (let i = children.length - 1; i >= 0; i--) {
      if (children[i].ParentID === k.ID) {
        kChildren.push(...children.splice(i, 1));
      }
    }
    return { ...k, Children: kChildren };
  });

  const filtered = gravidKnocs.filter(
    (k) => !k.HideFromAdmins && k.Name.toLowerCase().includes(search.value.toLowerCase()),
  );
  let sorted = filtered.sort(globals.smartSortFunc(globals.sortObjBy(sortBy.value)));
  return sortDesc.value ? sorted.reverse() : sorted;
});

function aclFullUrl(knoc) {
  return `${window.location.protocol}//${window.location.hostname}${window.location.port ? `:${window.location.port}` : ""}/api/v1/allowlists/${knoc.ID}/${knoc.AclConfig.allowlist.Token}.txt`;
}

function haproxyAdminCmd(knoc) {
  var pipeTo = "",
    cmd = "";
  if (knoc.BackendConfig?.haproxy) {
    cmd = `show acl #${knoc.AclConfig.haproxy.ID}`;
    if (knoc.BackendConfig.haproxy.Protocol === "unix") {
      pipeTo = `socat stdio ${knoc.BackendConfig.haproxy.Address}`;
      return `echo "${cmd}" | ${pipeTo}`;
    } else if (knoc.BackendConfig.haproxy.Protocol === "tcp") {
      pipeTo = `nc ${knoc.BackendConfig.haproxy.Address.split(":")[0]} ${knoc.BackendConfig.haproxy.Address.split(":")[1]}`;
      return `echo "${cmd}" | ${pipeTo}`;
    }
  } else if (knoc.BackendConfig?.knocproxy) {
    cmd = `show map virt@knocknoc_tokens_${knoc.AclConfig.knocproxy.Name}.map`;
    if (knoc.BackendConfig.knocproxy.Protocol === "unix") {
      pipeTo = `socat stdio ${knoc.BackendConfig.knocproxy.Address}`;
      return `echo "${cmd}" | ${pipeTo}`;
    } else if (knoc.BackendConfig.knocproxy.Protocol === "tcp") {
      pipeTo = `nc ${knoc.BackendConfig.knocproxy.Address.split(":")[0]} ${knoc.BackendConfig.knocproxy.Address.split(":")[1]}`;
      return `echo "${cmd}" | ${pipeTo}`;
    }
  }
  return "";
}

function haproxyConfig(knoc) {
  if (!knoc.BackendConfig?.knocproxy) {
    return "";
  }

  const aclName = knoc.AclConfig.knocproxy.Name;
  return `# This is the Knocknoc configuration for the ${aclName} ACL in HAProxy.
# It should be placed inside the relevant 'backend' in your config.
# For more information, see https://docs.knocknoc.io/books/admin-guide/page/haproxy
http-request set-var(txn.new_cookie_value) urlp(knoctoken),map_str(virt@knocknoc_tokens_${aclName}.map,"") if { urlp(knoctoken) -m found }
acl new_knocknoc_cookie var(txn.new_cookie_value) -m len gt 0
acl existing_knocknoc_cookie cookie(KnocknocAuth),map(virt@knocknoc_cookies_${aclName}.map) -m found
http-request deny unless existing_knocknoc_cookie || new_knocknoc_cookie
http-response add-header Set-Cookie 'KnocknocAuth=%[var(txn.new_cookie_value)]; Max-Age=25200; Path=/; HttpOnly; Secure; SameSite=Lax' if new_knocknoc_cookie
http-request del-map(virt@knocknoc_tokens_${aclName}.map) %[urlp(knoctoken)] if { urlp(knoctoken) -m found }
http-request set-uri %[url,regsub(&?knoctoken=[^&]*,)] if { urlp(knoctoken) -m found }
http-request replace-header Cookie (.*)KnocknocAuth=[^;]*;?(.*) \\1\\2 if { req.cook(KnocknocAuth) -m found }
`;
}

// Test Grant dialog state
const showTestGrantDialog = ref(false);
const testGrantKnocId = ref(null);

// Test Grant functions
function openTestGrantDialog(knocId) {
  testGrantKnocId.value = knocId;
  showTestGrantDialog.value = true;
}

function handleTestGrantSuccess(message) {
  infoMessage.value = message;
}

function handleTestGrantError(error) {
  errorMessage.value = error;
}

const viewKnocId = ref(null);
const viewKnoc = computed(() => {
  return filteredData.value.find((k) => k.ID === viewKnocId.value);
});

const deleteKnocId = ref(null);
const deleteKnoc = computed(() => {
  return filteredData.value.find((k) => k.ID === deleteKnocId.value);
});

const apiKeyToDelete = ref(null);

async function handleDeleteKnoc() {
  // This means that this ACL was the last one assigned to the api key
  const apiKey = apiKeys.value.find((k) => k.ID === deleteKnoc.value?.AclConfig?.allowlist?.ApiKeyId);
  if (apiKey && apiKey.Scopes.length <= 1) {
    apiKeyToDelete.value = apiKey;
  }

  const delKnoc = async (knoc) => {
    try {
      await userService.deleteAcl(knoc.ID);

      const backend = backends.value.find((b) => b.ID === knoc.BackendID);
      if (backend) {
        await userService.deleteBackend(backend.ID);
      }
      return true;
    } catch (error) {
      errorMessage.value = globals.renderError(t, error);
      return false;
    }
  };

  // This is necessary to clean up the backends of knocs
  const childKnocs = knocs.value.filter((k) => k.ParentID === deleteKnoc.value.ID);
  for (const knoc of childKnocs) {
    const success = await delKnoc(knoc);
    if (!success) {
      break;
    }
  }

  await delKnoc(deleteKnoc.value);

  fetchData();

  deleteKnocId.value = null;
}

async function handleDeleteApiKey() {
  try {
    userService.deleteApiKey(apiKeyToDelete.value.ID);
  } catch (error) {
    errorMessage.value = globals.renderError(t, error);
  }

  fetchData();
  apiKeyToDelete.value = null;
}

function fetchData() {
  userStore.fetchKnocs({ force: true }).then(
    (response) => {
      knocs.value = response.data;
    },
    (error) => {
      errorMessage.value = globals.renderError(t, error);
    },
  );

  userStore.fetchBackends({ force: true }).then(
    (response) => {
      backends.value = response.data;
    },
    (error) => {
      errorMessage.value = globals.renderError(t, error);
    },
  );

  userStore.fetchGroups({ force: true }).then(
    (response) => {
      groups.value = response.data;
    },
    (error) => {
      errorMessage.value = globals.renderError(t, error);
    },
  );

  userStore.fetchApiKeys({ force: true }).then(
    (response) => {
      apiKeys.value = response.data;
    },
    (error) => {
      errorMessage.value = globals.renderError(t, error);
    },
  );
}

watch(initialFocus, async (el) => {
  if (el) {
    await nextTick();
    el.focus();
  }
});

onMounted(() => {
  fetchData();

  // This is stupid, but just avoids a race condition when updating a knoc and receiving incorrect user/group counts
  setTimeout(() => {
    userStore.fetchKnocs({ force: true }).then(
      (response) => {
        knocs.value = response.data;
      },
      (error) => {
        errorMessage.value = globals.renderError(t, error);
      },
    );
  }, 1000);

  if (route.query.didUpdate) {
    infoMessage.value = "Knoc updated successfully";
    history.pushState(null, null, route.path);
  }

  if (Boolean(route.query.viewKnocId)) {
    viewKnocId.value = route.query.viewKnocId;
    history.pushState(null, null, route.path);
  }

  window.addEventListener("scroll", updateMenuItemPositions);
  window.addEventListener("resize", updateMenuItemPositions);
});

onUnmounted(() => {
  window.removeEventListener("scroll", updateMenuItemPositions);
  window.removeEventListener("resize", updateMenuItemPositions);
});

watch(() => menuButtonsRefs.value.length, updateMenuItemPositions);
</script>
